import { Button, Tabs } from '@mantine/core';
import type { MatchSuggestion } from '../../types';
import useConfirmMatches from '../hooks/useConfirmMatches';
import useRejectMatches from '../hooks/useRejectMatches';
import useUnlinkMatch from '../hooks/useUnlinkMatch';
import AmbiguousGroupCards from './AmbiguousGroupCards';
import SuggestionReviewTable from './SuggestionReviewTable';

type MatchingSectionNavProps = {
  suggestions: MatchSuggestion[];
};

export default function MatchingSectionNav({ suggestions }: MatchingSectionNavProps) {
  const highConfidence = suggestions.filter(s => s.confidence === 'HIGH');
  const mediumConfidence = suggestions.filter(s => s.confidence === 'MEDIUM');
  const ambiguous = suggestions.filter(s => s.confidence === 'MANUAL_REVIEW');

  const highPendingCount = highConfidence.filter(s => s.status === 'PENDING').length;
  const mediumPendingCount = mediumConfidence.filter(s => s.status === 'PENDING').length;
  const ambiguousPendingCount = ambiguous.filter(s => s.status === 'PENDING').length;

  const confirmMatches = useConfirmMatches();
  const rejectMatches = useRejectMatches();
  const unlinkMatch = useUnlinkMatch();

  return (
    <Tabs orientation="vertical" variant="vertical" defaultValue="high">
      <Tabs.List>
        <Tabs.Tab value="high">High Confidence ({highPendingCount})</Tabs.Tab>
        <Tabs.Tab value="medium">Medium Confidence ({mediumPendingCount})</Tabs.Tab>
        <Tabs.Tab value="ambiguous">Ambiguous ({ambiguousPendingCount})</Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="high" pl="lg">
        <SuggestionReviewTable
          suggestions={highConfidence}
          headerAction={
            highPendingCount > 0 && (
              <Button
                onClick={() =>
                  confirmMatches.mutate(
                    highConfidence.filter(s => s.status === 'PENDING').map(s => s.id),
                  )
                }
                loading={confirmMatches.isPending}
              >
                Confirm all suggested matches ({highPendingCount})
              </Button>
            )
          }
          onConfirm={id => confirmMatches.mutate([id])}
          onReject={id => rejectMatches.mutate([id])}
          onUnlink={id => {
            const suggestion = highConfidence.find(s => s.id === id);
            if (suggestion) unlinkMatch.mutate(suggestion.suggestedAsset.id);
          }}
          // TODO: backend needs a reopen endpoint
          onReopen={() => {}}
        />
      </Tabs.Panel>

      <Tabs.Panel value="medium" pl="lg">
        <SuggestionReviewTable
          suggestions={mediumConfidence}
          headerAction={
            mediumPendingCount > 0 && (
              <Button
                onClick={() =>
                  confirmMatches.mutate(
                    mediumConfidence.filter(s => s.status === 'PENDING').map(s => s.id),
                  )
                }
                loading={confirmMatches.isPending}
              >
                Confirm all suggested matches ({mediumPendingCount})
              </Button>
            )
          }
          onConfirm={id => confirmMatches.mutate([id])}
          onReject={id => rejectMatches.mutate([id])}
          onUnlink={id => {
            const suggestion = mediumConfidence.find(s => s.id === id);
            if (suggestion) unlinkMatch.mutate(suggestion.suggestedAsset.id);
          }}
          onReopen={() => {}}
        />
      </Tabs.Panel>

      <Tabs.Panel value="ambiguous" pl="lg">
        <AmbiguousGroupCards
          suggestions={ambiguous}
          onConfirm={id => confirmMatches.mutate([id])}
          onReject={id => rejectMatches.mutate([id])}
          onUnlink={id => {
            const suggestion = ambiguous.find(s => s.id === id);
            if (suggestion) unlinkMatch.mutate(suggestion.suggestedAsset.id);
          }}
          onReopen={() => {}}
          onRejectGroup={ids => rejectMatches.mutate(ids)}
        />
      </Tabs.Panel>
    </Tabs>
  );
}
